import {
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  ThemeProvider,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { ITheme, theme } from "../constants/theme";
import { buttonHoverAndActive } from "../utils/styles/buttonStyles";
import { useHistory } from "react-router-dom";
import BackButton from "../components-atoms/BackButton";
import { Stamp } from "../components-atoms/Stamp";
import ThreeDotMenu from "./ThreeDotMenu";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import CreditCard from '@material-ui/icons/CreditCard';
import { SubscriptionActivateModal } from "./SubscriptionActivateModal";
import { SubscriptionCardModal } from "../utils/subscriptions";
import { SubscriptionSuspendModal } from "./SubscriptionSuspendModal";
import { SubscriptionAddTrancheModal } from "./SubscriptionAddTrancheModal";
import { activateMerchantSubscription, activateProSubscription, addProTeamDeliveryTranche, suspendMerchantSubscription, suspendProTeamSubscription } from "../redux/actions/subscriptions";

interface SubscriptionDetailHeaderProps {
  name?: string;
  type: "Merchant" | "Pro";
  status?: string;
  ProTeamId?: string;
  MerchantId?: string;
  totalCreditsRemaining?: number;
  isAdmin?: boolean;
}

export const SubscriptionDetailHeader = ({ name, type, status, totalCreditsRemaining, ProTeamId, MerchantId, isAdmin }: SubscriptionDetailHeaderProps) => {
  const history = useHistory<{ from: string; }>();
  const classes = useStyles();
  const [visibleModal, setVisibleModal] = useState<SubscriptionCardModal>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(false);
  }, [status, totalCreditsRemaining]);


  const onActivate = (numberOfCredits: number, price: number) => {
    if (!MerchantId) return;
    setLoading(true);
    activateMerchantSubscription(
      { MerchantId, NumberOfCredits: numberOfCredits, Price: Math.round(price * 100) },
      () => { onCloseModal(); },
      (err) => { }
    );
  };

  const onSuspend = () => {
    if (!MerchantId) return;
    setLoading(true);
    suspendMerchantSubscription(
      { MerchantId },
      () => { onCloseModal(); },
      (err) => { }
    );
  };

  const onProActivate = (numberOfCredits: number, price: number) => {
    if (!ProTeamId) return;
    setLoading(true);
    activateProSubscription(
      { ProTeamId, NumberOfCredits: numberOfCredits, Price: Math.round(price * 100) },
      () => { onCloseModal(); },
      () => { }
    );
  };

  const onProSuspend = () => {
    if (!ProTeamId) return;
    setLoading(true);
    suspendProTeamSubscription(
      { ProTeamId },
      () => { onCloseModal(); },
      () => { }
    );
  };

  const onAddProDeliveryTranche = (numberOfCredits: number, price: number) => {
    if (!ProTeamId) return;
    setLoading(true);
    addProTeamDeliveryTranche(
      { ProTeamId, NumberOfCredits: numberOfCredits, Price: Math.round(price * 100) },
      () => { onCloseModal(); },
      () => { }
    );
  };

  function onCloseModal() {
    setVisibleModal(undefined);
  }

  return (
    <Container className={classes.header}>
      <ThemeProvider theme={theme}>
        <Grid container direction="row">
          {isAdmin &&
            <Grid xs={1} item container>
              <Grid
                item
                container
                alignItems="center"
                justifyContent="center"
                xs={12}>

                <BackButton
                  onClick={() => history.goBack()}
                />
              </Grid>
            </Grid>
          }
          <Grid item xs={6} className={classes.headerOrderDetailSection}>
            <Grid item>
              <Typography
                variant="body1"
                component="p"
                color="textSecondary"
                className={classes.headerSubtitle}>
                {type}
              </Typography>
            </Grid>
            <Grid item container>
              <Grid item>
                <Typography variant="h5" component="h1" color="textPrimary">
                  {name}
                </Typography>
              </Grid>
              {status && <Stamp type="fail" text={status} />}
            </Grid>
          </Grid>
          <Grid
            item
            alignItems="center"
            justifyContent="flex-end"
            container
            className={classes.headerButtonContainer}
            xs={5}>
            {loading ?
              <CircularProgress size={24} /> :
              !isAdmin ? <></> :
                <ThreeDotMenu>
                  {status === "Inactive"
                    ?
                    <MenuItem
                      style={{ color: theme.palette.success.main }}
                      onClick={() => setVisibleModal(SubscriptionCardModal.ACTIVATE)}
                    >
                      <CheckCircleIcon />
                      <Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
                        {type === "Pro" ? "Activate subscription" : "Set subscription price"}
                      </Typography>
                    </MenuItem>
                    :
                    <MenuItem
                      style={{ color: theme.palette.error.main }}
                      onClick={() => setVisibleModal(SubscriptionCardModal.SUSPEND)}
                    >
                      <DeleteIcon />
                      <Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
                        Suspend subscription
                      </Typography>
                    </MenuItem>
                  }
                  {type === "Pro" ?
                    <MenuItem
                      onClick={() => setVisibleModal(SubscriptionCardModal.ADD_DELIVERY_TRANCHE)}>
                      <CreditCard />
                      <Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
                        Add delivery tranche
                      </Typography>
                    </MenuItem>
                    : null
                  }
                </ThreeDotMenu>
            }
          </Grid>
        </Grid>

        {visibleModal !== undefined &&
          <>
            <SubscriptionActivateModal
              isVisible={visibleModal === SubscriptionCardModal.ACTIVATE}
              close={onCloseModal}
              activate={type === "Pro" ? onProActivate : onActivate}
            />
            <SubscriptionSuspendModal
              isVisible={visibleModal === SubscriptionCardModal.SUSPEND}
              close={onCloseModal}
              suspend={type === "Pro" ? onProSuspend : onSuspend}
            />
            <SubscriptionAddTrancheModal
              // @ts-ignore
              isVisible={visibleModal === SubscriptionCardModal.ADD_DELIVERY_TRANCHE}
              close={onCloseModal}
              addTranche={onAddProDeliveryTranche}
            />
          </>
        }

      </ThemeProvider>
    </Container >
  );
};
const useStyles = makeStyles((theme: ITheme) => ({
  header: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  headerOrderDetailSection: {
    paddingLeft: 16,
  },
  headerSubtitle: {
    marginBottom: 4,
  },
  headerButtonContainer: {
    paddingRight: 24,
  },
  primaryButton: {
    marginLeft: 8,
    borderRadius: 8,
    ...buttonHoverAndActive

  }
}));
